import PropTypes from 'prop-types';
import React from 'react';
import bindAll from 'lodash.bindall';
import ConnectionModalComponent, {PHASES} from '../components/connection-modal/connection-modal.jsx';
import VM from 'scratch-vm';
import analytics from '../lib/analytics';
import extensionData from '../lib/libraries/extensions/index.jsx';
import {connect} from 'react-redux';
import {trConnectionModal} from '../components/debug-modal/sections/tr-messages';

import {closeConnectionModal} from '../reducers/modals';
import {isMicroBitUpdateSupported, selectAndUpdateMicroBit} from '../lib/microbit-update';
import SerialHelper from '../lib/serial-helper';
import VMExtensionBridge from '../lib/vm-extension-bridge';

// Bluetooth ve Serial ikonları
import microbitIconURL from '../lib/libraries/extensions/microbit/microbit-illustration.svg';
import serialIconURL from '../lib/libraries/extensions/gdxfor/gdxfor-illustration.svg';

// Türkçe içeriği kullanmak için TRUE değerini ayarlayın
const useTurkish = true;

class ConnectionModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleScanning',
            'handleCancel',
            'handleConnected',
            'handleConnecting',
            'handleDisconnect',
            'handleError',
            'handleHelp',
            'handleSendUpdate',
            'handleUpdatePeripheral',
            'handleOpenSerial',
            'handleConnectSerial',
            'handleSerialRefresh',
            'handleSelectDeviceType'
        ]);
        
        // Serial yardımcısını oluştur
        this.serialHelper = new SerialHelper();
        
        // VM Extension Bridge oluştur
        this.vmBridge = new VMExtensionBridge(this.props.vm);
        
        this.state = {
            extension: extensionData.find(ext => ext.extensionId === props.extensionId),
            phase: PHASES.deviceSelection,
            isSerialSupported: this.serialHelper.isSerialSupported()
        };
    }
    componentDidMount () {
        this.props.vm.on('PERIPHERAL_CONNECTED', this.handleConnected);
        this.props.vm.on('PERIPHERAL_REQUEST_ERROR', this.handleError);
        
        // Modal açıldığında body scroll'unu engelle
        document.body.classList.add('modal-open');
    }
    componentWillUnmount () {
        this.props.vm.removeListener('PERIPHERAL_CONNECTED', this.handleConnected);
        this.props.vm.removeListener('PERIPHERAL_REQUEST_ERROR', this.handleError);
        
        // Seri port bağlantısını kapat - güvenli kontrol
        if (this.serialHelper && typeof this.serialHelper.isConnected === 'function') {
            if (this.serialHelper.isConnected()) {
                this.serialHelper.disconnect();
            }
        }
        
        // Modal kapandığında body scroll'unu geri aç
        document.body.classList.remove('modal-open');
    }
    handleSelectDeviceType (deviceType) {
        if (deviceType === 'bluetooth') {
            this.setState({
                phase: PHASES.scanning
            });
            
            analytics.event({
                category: 'extensions',
                action: 'bluetooth selected',
                label: this.props.extensionId
            });
        } else if (deviceType === 'serial') {
            this.setState({
                phase: PHASES.serial
            });
            
            analytics.event({
                category: 'extensions',
                action: 'serial selected',
                label: 'serial'
            });
        }
    }
    handleScanning () {
        this.setState({
            phase: PHASES.scanning
        });
    }
    handleConnecting (peripheralId) {
        this.props.vm.connectPeripheral(this.props.extensionId, peripheralId);
        this.setState({
            phase: PHASES.connecting
        });
        analytics.event({
            category: 'extensions',
            action: 'connecting',
            label: this.props.extensionId
        });
    }
    handleDisconnect () {
        try {
            this.props.vm.disconnectPeripheral(this.props.extensionId);
        } finally {
            this.props.onCancel();
        }
    }
    handleCancel () {
        try {
            // ExtensionId kontrolü
            const extensionId = this.props.extensionId || 'unknown';
            
            // If we're not connected to a peripheral, close the websocket so we stop scanning.
            if (this.props.vm && this.props.vm.getPeripheralIsConnected) {
                if (!this.props.vm.getPeripheralIsConnected(extensionId)) {
                    this.props.vm.disconnectPeripheral(extensionId);
                }
            }
            
            // Seri port bağlantısını kapat - güvenli kontrol
            if (this.serialHelper && typeof this.serialHelper.isConnected === 'function') {
                if (this.serialHelper.isConnected()) {
                    this.serialHelper.disconnect();
                }
            }
        } catch (error) {
            console.error('Modal kapatma hatası:', error);
        } finally {
            // Close the modal.
            if (this.props.onCancel && typeof this.props.onCancel === 'function') {
                this.props.onCancel();
            }
        }
    }
    handleError () {
        // Assume errors that come in during scanning phase are the result of not
        // having scratch-link installed.
        if (this.state.phase === PHASES.scanning || this.state.phase === PHASES.unavailable) {
            this.setState({
                phase: PHASES.unavailable
            });
        } else {
            this.setState({
                phase: PHASES.error
            });
            analytics.event({
                category: 'extensions',
                action: 'connecting error',
                label: this.props.extensionId
            });
        }
    }
    handleConnected () {
        this.setState({
            phase: PHASES.connected
        });
        
        // ESP32 extension için özel işlem
        if (this.props.extensionId === 'serialExample') {
            // Extension'ı VM'e yükle
            if (this.props.vm && this.props.vm.extensionManager) {
                try {
                    // Extension zaten yüklü mü kontrol et
                    if (!this.props.vm.extensionManager.isExtensionLoaded('serialExample')) {
                        console.log('ESP32 Extension VM\'e yükleniyor...');
                        this.props.vm.extensionManager.loadExtensionURL('serialExample');
                    } else {
                        console.log('ESP32 Extension zaten yüklü');
                    }
                } catch (error) {
                    console.error('ESP32 Extension yükleme hatası:', error);
                }
            }
        }
        
        analytics.event({
            category: 'extensions',
            action: 'connected',
            label: this.props.extensionId
        });
    }
    handleHelp () {
        window.open(this.state.extension.helpLink, '_blank');
        analytics.event({
            category: 'extensions',
            action: 'help',
            label: this.props.extensionId
        });
    }
    handleUpdatePeripheral () {
        this.setState({
            phase: PHASES.updatePeripheral
        });
        analytics.event({
            category: 'extensions',
            action: 'enter peripheral update flow',
            label: this.props.extensionId
        });
    }
    
    // Seri port ile ilgili yeni fonksiyonlar
    handleOpenSerial () {
        this.setState({
            phase: PHASES.serial
        });
        analytics.event({
            category: 'extensions',
            action: 'open serial flow',
            label: 'serial'
        });
    }
    
    async handleConnectSerial (baudRate) {
        try {
            // VM Bridge üzerinden seri port bağlantısı kur
            const connected = await this.vmBridge.requestSerialConnection(baudRate);
            if (connected) {
                // Bağlantı başarılı, bağlantı tamamlandı modalına geç
                this.setState({
                    phase: PHASES.connected
                });
                
                analytics.event({
                    category: 'extensions',
                    action: 'serial connected',
                    label: 'serial'
                });
            }
            
            return connected;
        } catch (error) {
            console.error('Serial bağlantı hatası:', error);
            return false;
        }
    }
    
    handleSerialRefresh () {
        // Sayfayı yenile veya seri bağlantı penceresini sıfırla
        this.setState({
            phase: PHASES.serial
        });
    }
    
    /**
     * Handle sending an update to the peripheral.
     * @param {function(number): void} [progressCallback] Optional callback for progress updates in the range of [0..1].
     * @returns {Promise} Resolves when the update is complete.
     */
    handleSendUpdate (progressCallback = () => {}) {
        let previousProgress = 0;
        progressCallback(previousProgress);
        return this.props.vm.updatePeripheral(this.props.extensionId, previousProgress => {
            const progress = Math.max(previousProgress, previousProgress);
            progressCallback(progress);
            previousProgress = progress;
        });
    }
    
    render () {
        const canUpdatePeripheral = (this.props.extensionId === 'microbit') && isMicroBitUpdateSupported();
        
        // Eğer Türkçe içerik kullanılacaksa, bağlantı mesajlarını Türkçe olarak ayarla
        let connectingMessage = this.state.extension && this.state.extension.connectingMessage;
        if (useTurkish && this.state.extension) {
            if (this.props.extensionId === 'microbit') {
                connectingMessage = "Bağlanıyor. micro:bit'inizin açık olduğundan emin olun.";
            } else if (this.props.extensionId === 'ev3') {
                connectingMessage = "Bağlanıyor. EV3'ünüzün PIN kodunun 1234 olarak ayarlandığından emin olun.";
            } else if (this.props.extensionId === 'wedo2') {
                connectingMessage = "Bağlanıyor";
            } else if (this.props.extensionId === 'gdxfor') {
                connectingMessage = "Bağlanıyor";
            } else {
                connectingMessage = "Bağlanıyor";
            }
        }
        
        // Seri port modal başlığı
        let title = useTurkish ? this.getTurkishExtensionName(this.props.extensionId) : this.props.extensionId;
        if (this.props.extensionId === 'serial') {
            title = useTurkish ? 'Seri Port Bağlantısı' : 'Serial Port Connection';
        }
        
        // Cihaz seçim ekranı için başlık
        if (this.state.phase === PHASES.deviceSelection) {
            title = useTurkish ? 'Cihaz Türü Seçin' : 'Select Device Type';
        }
        
        return (
            <ConnectionModalComponent
                bluetoothIconURL={microbitIconURL}
                connectingMessage={connectingMessage}
                connectionIconURL={this.state.extension && this.state.extension.connectionIconURL}
                connectionSmallIconURL={this.state.extension && this.state.extension.connectionSmallIconURL}
                connectionTipIconURL={this.state.extension && this.state.extension.connectionTipIconURL}
                extensionId={this.props.extensionId || 'device-connection'}
                isSerialSupported={this.state.isSerialSupported}
                name={useTurkish && this.state.extension ? 
                    this.getTurkishExtensionName(this.state.extension.name) : 
                    (this.state.extension && this.state.extension.name)}
                phase={this.state.phase}
                serialIconURL={serialIconURL}
                title={title}
                useAutoScan={this.state.extension && this.state.extension.useAutoScan}
                vm={this.props.vm}
                onCancel={this.handleCancel}
                onConnected={this.handleConnected}
                onConnecting={this.handleConnecting}
                onConnectSerial={this.handleConnectSerial}
                onDisconnect={this.handleDisconnect}
                onHelp={this.handleHelp}
                onRefresh={this.handleSerialRefresh}
                onScanning={this.handleScanning}
                onSelectDeviceType={this.handleSelectDeviceType}
                onSendPeripheralUpdate={canUpdatePeripheral ? this.handleSendUpdate : null}
                onUpdatePeripheral={canUpdatePeripheral ? this.handleUpdatePeripheral : null}
            />
        );
    }

    // Uzantı isimlerini Türkçe'ye çeviren yardımcı fonksiyon
    getTurkishExtensionName (name) {
        const translations = {
            'micro:bit': 'micro:bit',
            'LEGO MINDSTORMS EV3': 'LEGO MINDSTORMS EV3',
            'LEGO Education WeDo 2.0': 'LEGO Education WeDo 2.0',
            'Go Direct Force & Acceleration': 'Go Direct Kuvvet ve İvme',
            'ev3': 'EV3',
            'wedo2': 'WeDo 2.0',
            'gdxfor': 'Kuvvet ve İvme Sensörü',
            'serial': 'Seri Port'
        };
        
        return translations[name] || name;
    }
}

ConnectionModal.propTypes = {
    extensionId: PropTypes.string.isRequired,
    onCancel: PropTypes.func.isRequired,
    vm: PropTypes.instanceOf(VM).isRequired
};

const mapStateToProps = state => ({
    extensionId: state.scratchGui.connectionModal.extensionId
});

const mapDispatchToProps = dispatch => ({
    onCancel: () => {
        dispatch(closeConnectionModal());
    }
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ConnectionModal);
